import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { fetchRelationships } from '../redux/actions/relationshipActions';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  Paper,
  Chip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BreadcrumbNav from '../components/BreadcrumbNav';
import CancelButton from '../components/CancelButton';
import SubmitButton from '../components/SubmitButton';
import { textFieldStyles } from '../styles/textFieldStyles';

const CreateWorkflowPage = () => {
  const { t } = useTranslation(['workflows', 'buttons', 'forms', 'common', 'navigation', 'messages']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { relationships } = useSelector((state) => state.relationshipList || { relationships: [] });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [appliesToAnchorEl, setAppliesToAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    enabled: true,
    trigger: {
      type: 'template_used',
      filters: { templateIds: [], emailTemplateIds: [] },
    },
    steps: [],
  });

  useEffect(() => {
    fetchTemplates();
    fetchEmailTemplates();
    dispatch(fetchRelationships());
  }, [dispatch]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/document-templates');
      setTemplates(response.data);
    } catch (err) {
      console.error('Error fetching document templates:', err);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get('/api/email-templates');
      setEmailTemplates(response.data);
    } catch (err) {
      console.error('Error fetching email templates:', err);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child, subChild] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: subChild ? {
            ...prev[parent][child],
            [subChild]: value,
          } : value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
    setError('');
  };

  const DEFAULT_PROMPT_BY_ACTION = {
    suggest_template: 'Do you want to suggest this template to the recipient?',
    create_task: 'Do you want to create this task?',
    show_notification: 'Do you want to send this notification?',
    create_calendar_event: 'Do you want to create this calendar event?',
    prompt_time_entry: 'Do you want to log time for this case?',
    send_email: 'Do you want me to inform the client?',
  };

  const handleAddStep = () => {
    const newOrder = formData.steps.length > 0 
      ? Math.max(...formData.steps.map(s => s.order || 0)) + 1 
      : 1;
    
    setFormData(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          order: newOrder,
          actionType: 'show_notification',
          config: {},
          requireConfirmation: false,
          promptMessage: DEFAULT_PROMPT_BY_ACTION.show_notification,
        },
      ],
    }));
  };

  const handleDeleteStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  };

  const handleStepChange = (index, field, value) => {
    const actionPromptDefaults = {
      suggest_template: t('workflows:prompt_default_suggest_template') || 'Do you want to suggest this template to the recipient?',
      create_task: t('workflows:prompt_default_create_task') || 'Do you want to create this task?',
      show_notification: t('workflows:prompt_default_send_notification') || 'Do you want to send this notification?',
      create_calendar_event: t('workflows:prompt_default_calendar_event') || 'Do you want to create this calendar event?',
      prompt_time_entry: t('workflows:prompt_default_time_entry') || 'Do you want to log time for this case?',
      send_email: t('workflows:prompt_default_send_email') || 'Do you want me to inform the client?',
    };
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => {
        if (i === index) {
          if (field === 'actionType') {
            const defaultPrompt = actionPromptDefaults[value] || '';
            return {
              ...step,
              actionType: value,
              config: {},
              promptMessage: defaultPrompt,
            };
          } else if (field.startsWith('config.')) {
            const configField = field.replace('config.', '');
            return {
              ...step,
              config: {
                ...step.config,
                [configField]: value,
              },
            };
          } else {
            return {
              ...step,
              [field]: value,
            };
          }
        }
        return step;
      }),
    }));
  };

  const isFormValid = () => {
    if (!formData.name.trim()) {
      return false;
    }
    if (!formData.trigger.type) {
      return false;
    }
    if (formData.steps.length === 0) {
      return false;
    }
    const orders = (formData.steps || []).map(s => Number(s.order));
    if ([...new Set(orders)].length !== orders.length) {
      return false;
    }
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      if (!step.order && step.order !== 0) {
        return false;
      }
      if (!step.actionType) {
        return false;
      }
    }
    return true;
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setSnackbarMessage(t('workflows:validation.name_required') || 'Workflow name is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    }
    if (!formData.trigger.type) {
      setSnackbarMessage(t('workflows:validation.trigger_required') || 'Trigger type is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    }
    if (formData.steps.length === 0) {
      setSnackbarMessage(t('workflows:validation.steps_required') || 'At least one step is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    }
    const orders = (formData.steps || []).map(s => Number(s.order));
    const uniqueOrders = [...new Set(orders)];
    if (uniqueOrders.length !== orders.length) {
      setSnackbarMessage(t('workflows:validation.step_order_duplicate') || 'Each step must have a unique order. Two or more steps have the same order.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    }
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      if (!step.order && step.order !== 0) {
        setSnackbarMessage(t('workflows:validation.step_order_required', { index: i + 1 }) || `Step ${i + 1}: Order is required`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false;
      }
      if (!step.actionType) {
        setSnackbarMessage(t('workflows:validation.step_action_required', { index: i + 1 }) || `Step ${i + 1}: Action type is required`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false;
      }
      if (step.actionType === 'send_email') {
        if (!step.config?.emailTemplateId) {
          setSnackbarMessage(t('workflows:validation.send_email_template_required') || `Step ${i + 1}: Send Email requires an email template`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return false;
        }
        if (step.config?.recipientType === 'relationship' && !step.config?.recipient) {
          setSnackbarMessage(t('workflows:validation.send_email_recipient_required') || `Step ${i + 1}: Send Email requires a recipient when "Specific recipient" is selected`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const sortedSteps = [...formData.steps].sort((a, b) => (a.order || 0) - (b.order || 0));
      const filters = formData.trigger.filters || {};
      const workflowData = {
        ...formData,
        steps: sortedSteps,
        trigger: {
          ...formData.trigger,
          filters: {
            templateIds: filters.templateIds || [],
            emailTemplateIds: filters.emailTemplateIds || [],
          },
        },
      };

      await axios.post('/api/workflows', workflowData);
      setSnackbarMessage(t('workflows:created_successfully') || 'Workflow created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/workflows');
      }, 1500);
    } catch (error) {
      console.error('Error creating workflow:', error);
      setSnackbarMessage(error.response?.data?.message || t('messages:error.saving_workflow') || 'Error creating workflow');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const renderActionConfig = (step, index) => {
    const actionType = step.actionType;

    if (actionType === 'suggest_template') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={relationships || []}
              value={relationships?.find(r => r._id === step.config.recipient) || null}
              onChange={(event, newValue) => {
                handleStepChange(index, 'config.recipient', newValue?._id || '');
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.contractType === 'Individual') {
                  return `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim() || option.businessName || '';
                }
                return option.businessName || option.firstName || '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('workflows:recipient') || 'Recipient'}
                  placeholder={t('workflows:recipient_placeholder') || 'Select recipient'}
                  size="small"
                  sx={textFieldStyles()}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:message') || 'Message'}
              value={step.config.message || ''}
              onChange={(e) => handleStepChange(index, 'config.message', e.target.value)}
              multiline
              rows={3}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:variable_helper') || 'Use {{document.title}} or {{caseFile.name}} for dynamic values'}
            />
          </Grid>
        </Grid>
      );
    }

    if (actionType === 'create_task') {
      const taskTypes = [
        'Synchronization with Outlook',
        'Task in the procedure',
        'Other task',
        'Task directed towards the client',
        'Phone note (lawyer) opposing party',
        'Phone note client',
        'Phone note third party',
      ];

      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={relationships || []}
              value={relationships?.find(r => r._id === step.config.assignee) || null}
              onChange={(event, newValue) => {
                handleStepChange(index, 'config.assignee', newValue?._id || '');
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.contractType === 'Individual') {
                  return `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim() || option.businessName || '';
                }
                return option.businessName || option.firstName || '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('workflows:assignee') || 'Assignee'}
                  placeholder={t('workflows:assignee_placeholder') || 'Select assignee'}
                  size="small"
                  sx={textFieldStyles()}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:subject') || 'Subject'}
              value={step.config.subject || ''}
              onChange={(e) => handleStepChange(index, 'config.subject', e.target.value)}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:variable_helper') || 'Use {{document.title}} for dynamic values'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:description') || 'Description'}
              value={step.config.description || ''}
              onChange={(e) => handleStepChange(index, 'config.description', e.target.value)}
              multiline
              rows={3}
              size="small"
              sx={textFieldStyles()}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('workflows:due_date') || 'Due Date'}
              value={step.config.dueDate || ''}
              onChange={(e) => handleStepChange(index, 'config.dueDate', e.target.value)}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:due_date_helper') || 'Use "+7 days" for relative dates or ISO date format'}
              placeholder="+7 days"
            />
          </Grid>
        </Grid>
      );
    }

    if (actionType === 'show_notification') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={relationships || []}
              value={relationships?.find(r => r._id === step.config.recipient) || null}
              onChange={(event, newValue) => {
                handleStepChange(index, 'config.recipient', newValue?._id || '');
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.contractType === 'Individual') {
                  return `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim() || option.businessName || '';
                }
                return option.businessName || option.firstName || '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('workflows:recipient') || 'Recipient'}
                  placeholder={t('workflows:recipient_placeholder') || 'Select recipient'}
                  size="small"
                  sx={textFieldStyles()}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:message') || 'Message'}
              value={step.config.message || ''}
              onChange={(e) => handleStepChange(index, 'config.message', e.target.value)}
              multiline
              rows={3}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:variable_helper') || 'Use {{document.title}} for dynamic values'}
            />
          </Grid>
        </Grid>
      );
    }

    if (actionType === 'create_calendar_event') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={relationships || []}
              value={relationships?.find(r => r._id === step.config.owner) || null}
              onChange={(event, newValue) => {
                handleStepChange(index, 'config.owner', newValue?._id || '');
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.contractType === 'Individual') {
                  return `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim() || option.businessName || '';
                }
                return option.businessName || option.firstName || '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('workflows:owner') || 'Owner'}
                  placeholder={t('workflows:assignee_placeholder') || 'Select owner'}
                  size="small"
                  sx={textFieldStyles()}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:subject') || 'Subject'}
              value={step.config.subject || ''}
              onChange={(e) => handleStepChange(index, 'config.subject', e.target.value)}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:variable_helper') || 'Use {{caseFile.name}} for dynamic values'}
              placeholder="e.g. Case {{caseFile.name}} — Follow-up from insurance?"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:description') || 'Description'}
              value={step.config.description || ''}
              onChange={(e) => handleStepChange(index, 'config.description', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={textFieldStyles()}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:start_date') || 'Start Date'}
              value={step.config.startDate || ''}
              onChange={(e) => handleStepChange(index, 'config.startDate', e.target.value)}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:due_date_helper') || 'Use "+3 weeks", "+7 days", or ISO date'}
              placeholder="+3 weeks"
            />
          </Grid>
        </Grid>
      );
    }

    if (actionType === 'prompt_time_entry') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={relationships || []}
              value={relationships?.find(r => r._id === step.config.recipient) || null}
              onChange={(event, newValue) => {
                handleStepChange(index, 'config.recipient', newValue?._id || '');
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.contractType === 'Individual') {
                  return `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim() || option.businessName || '';
                }
                return option.businessName || option.firstName || '';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('workflows:recipient') || 'Recipient'}
                  placeholder={t('workflows:recipient_placeholder') || 'Select recipient'}
                  size="small"
                  sx={textFieldStyles()}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('workflows:message') || 'Message'}
              value={step.config.message || ''}
              onChange={(e) => handleStepChange(index, 'config.message', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={textFieldStyles()}
              helperText={t('workflows:variable_helper') || 'Use {{caseFile.name}} for dynamic values'}
              placeholder="Log time for {{caseFile.name}}?"
            />
          </Grid>
        </Grid>
      );
    }

    if (actionType === 'send_email') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={emailTemplates || []}
              value={emailTemplates?.find(t => (t._id || t.value) === step.config.emailTemplateId) || null}
              onChange={(event, newValue) => {
                handleStepChange(index, 'config.emailTemplateId', newValue?._id || '');
              }}
              getOptionLabel={(option) => option.name || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('workflows:email_template') || 'Email template'}
                  placeholder={t('workflows:email_template_placeholder') || 'Select email template'}
                  size="small"
                  sx={textFieldStyles()}
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" sx={textFieldStyles()}>
              <InputLabel>{t('workflows:recipient_type') || 'Recipient'}</InputLabel>
              <Select
                value={step.config?.recipientType || 'case_client'}
                onChange={(e) => handleStepChange(index, 'config.recipientType', e.target.value)}
                label={t('workflows:recipient_type') || 'Recipient'}
              >
                <MenuItem value="case_client">{t('workflows:recipient_type_case_client') || 'Case client'}</MenuItem>
                <MenuItem value="relationship">{t('workflows:recipient_type_relationship') || 'Specific recipient'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(step.config?.recipientType || 'case_client') === 'relationship' && (
            <Grid item xs={12}>
              <Autocomplete
                options={relationships || []}
                value={relationships?.find(r => r._id === step.config.recipient) || null}
                onChange={(event, newValue) => {
                  handleStepChange(index, 'config.recipient', newValue?._id || '');
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.contractType === 'Individual') {
                    return `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim() || option.businessName || '';
                  }
                  return option.businessName || option.firstName || '';
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('workflows:recipient') || 'Recipient'}
                    placeholder={t('workflows:recipient_placeholder') || 'Select recipient'}
                    size="small"
                    sx={textFieldStyles()}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      );
    }

    return null;
  };

  const renderStepTypeField = (step, index) => {
    const actionType = step.actionType;
    if (actionType === 'show_notification') {
      const notificationTypes = ['File', 'Task', 'Timesheet', 'Document'];
      return (
        <FormControl fullWidth size="small" sx={textFieldStyles()}>
          <InputLabel>{t('workflows:notification_type') || 'Notification Type'}</InputLabel>
          <Select
            value={step.config?.type || 'Document'}
            onChange={(e) => handleStepChange(index, 'config.type', e.target.value)}
            label={t('workflows:notification_type') || 'Notification Type'}
          >
            {notificationTypes.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (actionType === 'create_task') {
      const taskTypes = ['Synchronization with Outlook', 'Task in the procedure', 'Other task', 'Task directed towards the client', 'Phone note (lawyer) opposing party', 'Phone note client', 'Phone note third party'];
      return (
        <FormControl fullWidth size="small" sx={textFieldStyles()}>
          <InputLabel>{t('workflows:task_type') || 'Task Type'}</InputLabel>
          <Select
            value={step.config?.type || 'Other task'}
            onChange={(e) => handleStepChange(index, 'config.type', e.target.value)}
            label={t('workflows:task_type') || 'Task Type'}
          >
            {taskTypes.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    return null;
  };

  const availableVariables = [
    '{{document.title}}',
    '{{document._id}}',
    '{{caseFile.name}}',
    '{{caseFile._id}}',
    '{{user.firstName}}',
    '{{user.lastName}}',
    '{{userId}}',
    '{{template.name}}',
    '{{currentDate}}',
    '{{currentTime}}',
  ];

  return (
    <Container maxWidth="lg" style={{ position: 'relative', paddingBottom: '80px' }}>
      <BreadcrumbNav
        title={t('workflows:create') || 'Create Workflow'}
        breadcrumbs={[
          { label: t('navigation:home'), href: '/' },
          { label: t('navigation:workflows'), href: '/workflows' },
        ]}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: 'white',
          padding: '30px 24px',
          marginTop: '24px',
        }}
      >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Basic Information */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 0, border: '1px solid #d1d1d1', boxShadow: 'none' }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', fontWeight: 500 }}>
            {t('workflows:basic_information') || 'Basic Information'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('workflows:name') || 'Name'}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                size="small"
                sx={textFieldStyles()}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  />
                }
                label={t('workflows:enabled') || 'Enabled'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('workflows:description') || 'Description'}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={2}
                size="small"
                sx={textFieldStyles()}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Trigger Configuration */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 0, border: '1px solid #d1d1d1', boxShadow: 'none' }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', fontWeight: 500 }}>
            {t('workflows:trigger_configuration') || 'Trigger Configuration'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={textFieldStyles()}>
                <InputLabel>{t('workflows:when_to_trigger') || 'When to Trigger'}</InputLabel>
                <Select
                  value={formData.trigger.type}
                  onChange={(e) => handleInputChange('trigger.type', e.target.value)}
                  label={t('workflows:when_to_trigger') || 'When to Trigger'}
                >
                  <MenuItem value="template_used">{t('workflows:document_from_template_created') || 'Document from template created'}</MenuItem>
                  <MenuItem value="email_sent">{t('workflows:email_template_sent') || 'Email template sent'}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {(() => {
                const isDocTemplates = formData.trigger.type === 'template_used';
                const options = isDocTemplates ? templates : emailTemplates;
                const ids = isDocTemplates ? (formData.trigger.filters.templateIds || []) : (formData.trigger.filters.emailTemplateIds || []);
                const filterKey = isDocTemplates ? 'trigger.filters.templateIds' : 'trigger.filters.emailTemplateIds';
                const summary = ids.length === 0 ? 'All templates' : `${ids.length} template(s) selected`;
                return (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      label="Applies To"
                      value={summary}
                      onClick={(e) => setAppliesToAnchorEl(e.currentTarget)}
                      readOnly
                      sx={textFieldStyles()}
                      helperText={t('workflows:template_filter_helper') || 'Leave empty for all templates, or select specific templates'}
                      InputProps={{
                        readOnly: true,
                        sx: { cursor: 'pointer' },
                      }}
                    />
                    <Popover
                      open={!!appliesToAnchorEl}
                      anchorEl={appliesToAnchorEl}
                      onClose={() => setAppliesToAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      PaperProps={{ sx: { maxHeight: 280, width: 320 } }}
                    >
                      <List dense disablePadding>
                        {options.map((opt) => {
                          const checked = ids.includes(opt._id);
                          return (
                            <ListItemButton
                              key={opt._id}
                              onClick={() => {
                                const next = checked ? ids.filter((id) => id !== opt._id) : [...ids, opt._id];
                                handleInputChange(filterKey, next);
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox edge="start" checked={checked} disableRipple size="small" />
                              </ListItemIcon>
                              <ListItemText primary={opt.name || ''} primaryTypographyProps={{ fontSize: '12px' }} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Popover>
                  </>
                );
              })()}
            </Grid>
          </Grid>
        </Paper>

        {/* Steps Configuration */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 0, border: '1px solid #d1d1d1', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>
              {t('workflows:steps') || 'Steps'}
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              size="small"
              variant="outlined"
            >
              {t('workflows:add_step') || 'Add Step'}
            </Button>
          </Box>

          {formData.steps.length === 0 ? (
            <Alert severity="info">
              {t('workflows:no_steps') || 'No steps configured. Add a step to define workflow actions.'}
            </Alert>
          ) : (
            formData.steps.map((step, index) => (
              <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9', borderRadius: 0, border: '1px solid #d1d1d1', boxShadow: 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    {t('workflows:step') || 'Step'} {step.order || index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteStep(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      label={t('workflows:order') || 'Order'}
                      type="number"
                      value={step.order || ''}
                      onChange={(e) => handleStepChange(index, 'order', parseInt(e.target.value) || 0)}
                      size="small"
                      sx={textFieldStyles()}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <FormControl fullWidth size="small" sx={textFieldStyles()}>
                      <InputLabel>{t('workflows:action_type') || 'Action Type'}</InputLabel>
                      <Select
                        value={step.actionType || ''}
                        onChange={(e) => handleStepChange(index, 'actionType', e.target.value)}
                        label={t('workflows:action_type') || 'Action Type'}
                        required
                      >
                        <MenuItem value="suggest_template">Suggest Template</MenuItem>
                        <MenuItem value="create_task">Create Task</MenuItem>
                        <MenuItem value="show_notification">{t('workflows:action_send_notification') || 'Send Notification'}</MenuItem>
                        <MenuItem value="create_calendar_event">Create Calendar Event</MenuItem>
                        <MenuItem value="prompt_time_entry">Prompt Time Entry</MenuItem>
                        <MenuItem value="send_email">{t('workflows:action_send_email') || 'Send Email'}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {(step.actionType === 'show_notification' || step.actionType === 'create_task') && (
                    <Grid item xs={12} md={4}>
                      {renderStepTypeField(step, index)}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    {renderActionConfig(step, index)}
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!step.requireConfirmation}
                          onChange={(e) => handleStepChange(index, 'requireConfirmation', e.target.checked)}
                        />
                      }
                      label={t('workflows:step_require_confirmation') || 'Require confirmation for this step'}
                    />
                  </Grid>
                  {step.requireConfirmation && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('workflows:step_prompt_message') || 'Prompt message for this step'}
                        value={step.promptMessage || ''}
                        onChange={(e) => handleStepChange(index, 'promptMessage', e.target.value)}
                        size="small"
                        sx={textFieldStyles()}
                        placeholder={t('workflows:prompt_message_placeholder') || 'Do you want to execute this workflow?'}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))
          )}
        </Paper>

        {/* Variable Helper */}
        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 0, border: '1px solid #d1d1d1', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InfoOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              {t('workflows:available_variables') || 'Available Variables'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('workflows:variable_usage') || 'You can use these variables in action configuration fields:'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableVariables.map((variable) => (
              <Chip
                key={variable}
                label={variable}
                size="small"
                sx={{ fontFamily: 'monospace' }}
              />
            ))}
          </Box>
        </Paper>

      </Box>
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          zIndex: 10,
          backgroundColor: 'white',
          padding: '13px 80px',
        }}
      >
        <CancelButton variant="outlined" onClick={() => navigate('/workflows')} size="small" disabled={loading}>
          {t('buttons:secondary.cancel') || 'Cancel'}
        </CancelButton>
        <SubmitButton onClick={handleSave} size="small" disabled={loading}>
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            'Save'
          )}
        </SubmitButton>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateWorkflowPage;
